import React, { useState, useEffect } from 'react';

import { send_request } from '../../../../utils/Request';

import { Security, LockOpen, Lock, Public } from '@mui/icons-material';
import { Tooltip, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { ComponentFactory } from '../../../ProjectCanvas/ComponentRegistry';
import { CanvasHeaderButton } from '../CanvasButtonsUtil/CanvasStyledComponents';

function AccessControl(props) {
  const { handleAccess, projectId, components, rerenderBadge, tooltips } = props;

  const [accessStatus, setAccessStatus] = useState(null);

  useEffect(() => {
    const fetchAccessStatus = async () => {
      let componentsWithAccess = [];

      for (let c of components) {
        if (ComponentFactory[c.type] && ComponentFactory[c.type].hasAccessControl) {
          let newC = {};
          newC['component_id'] = c['componentId'];
          newC['isAlwaysPublic'] = ComponentFactory[c.type].isAlwaysPublic;
          componentsWithAccess.push(newC);
        }
      }

      let projectComponentData = {
        projectId: String(projectId),
        components: componentsWithAccess,
      };

      try {
        const value = await send_request(
          'authz-service/get-project-access-status',
          projectComponentData,
          '',
          'post'
        );
        if (value.data != '') {
          setAccessStatus(value.data);
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchAccessStatus();
  }, [rerenderBadge]);

  const getStatusColor = status => {
    if (!status) return { bg: 'var(--color-action-hover)', text: 'var(--color-text-disabled)', border: 'var(--color-divider)' };

    switch (status.toLowerCase()) {
      case 'restricted':
        return { bg: 'var(--color-action-hover)', text: 'var(--color-error)', border: 'var(--color-error)' };
      case 'public':
        return { bg: 'var(--color-action-hover)', text: 'var(--color-success)', border: 'var(--color-success)' };
      case 'mixed':
        return { bg: 'var(--color-action-hover)', text: 'var(--color-warning)', border: 'var(--color-warning)' };
      default:
        return { bg: 'var(--color-action-hover)', text: 'var(--color-text-disabled)', border: 'var(--color-divider)' };
    }
  };

  const getStatusIcon = status => {
    if (!status) return <Security fontSize="inherit" />;

    switch (status.toLowerCase()) {
      case 'restricted':
        return <Lock fontSize="inherit" />;
      case 'public':
        return <Public fontSize="inherit" />;
      case 'mixed':
        return <LockOpen fontSize="inherit" />;
      default:
        return <Security fontSize="inherit" />;
    }
  };

  const StatusChip = styled(Box)(({ theme, status }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '5px',
    fontSize: '10px',
    lineHeight: 1.2,
    borderRadius: '4px',
    padding: '2px 5px',
    maxHeight: '18px',
    whiteSpace: 'nowrap',
    backgroundColor: getStatusColor(status).bg,
    color: getStatusColor(status).text,
    border: `1px solid ${getStatusColor(status).border}`,
    '& .MuiSvgIcon-root': {
      width: '14px',
      height: '14px',
      marginRight: '4px',
      verticalAlign: 'middle',
    },
    '& .status-text': {
      fontSize: '10px',
      lineHeight: 1.2,
      verticalAlign: 'middle',
    },
    '& .icon-container': {
      display: 'flex',
      alignItems: 'center',
    },
  }));

  const getStatusContent = status => {
    const formattedStatus = status.charAt(0) + status.slice(1).toLowerCase();

    return (
      <StatusChip status={status}>
        <Box component="span" className="icon-container">
          {getStatusIcon(status)}
        </Box>
        <span className="status-text">{formattedStatus}</span>
      </StatusChip>
    );
  };

  const getTooltipContent = status => {
    if (!status) return tooltips.access;

    switch (status.toLowerCase()) {
      case 'restricted':
        return 'This workflow has restricted access. Only specific users can view and edit components.';
      case 'public':
        return 'This workflow is public. All components can be accessed by anyone.';
      case 'mixed':
        return 'This workflow has mixed access settings. Some components are public, others are restricted.';
      default:
        return tooltips.access;
    }
  };

  let accessControl = (
    <Tooltip title={getTooltipContent(accessStatus)}>
      <CanvasHeaderButton
        onClick={handleAccess}
        variant={'outlined'}
        startIcon={<Security />}
        disabled={props.isCanvasLocked}
      >
        Access
        {accessStatus && getStatusContent(accessStatus)}
      </CanvasHeaderButton>
    </Tooltip>
  );

  return accessControl;
}

export default AccessControl;
